import DeviceAPI from 'api/device.api';
import {
  updateLoadingState,
  updateLatestSensorPollingState,
} from 'store/app.slice';
import {
  setDevicesWithTagList,
  setUnassociatedDeviceList,
  updateUnassociatedDeviceList,
  setLatestOperationalData,
  setLiveTagData,
  setIsLoadingSensorData,
  setIsLoadingOperationalData,
} from 'store/device.slice';
import { type Device } from 'types/device.types';
import { type IUpdateDevicePayload } from 'types/payloads/device.payload.types';
import AssetHandler from './asset.handler';
import { BaseHandler } from './base.handler';

export default class DeviceHandler extends BaseHandler {
  private readonly api: DeviceAPI;
  private readonly assetHandler: AssetHandler;

  constructor() {
    super();

    this.api = new DeviceAPI();
    this.assetHandler = new AssetHandler();
  }

  /**
   * Gets all unassociated devices from the backend for this particular customer
   * The devices are then stored in the redux store. Associated devices are retrieved with
   * in the asset handler using joins. This is to avoid programatically joining the devices
   * to the assets.
   *
   * @returns Promise<Device[]>
   *
   */
  async getAll(): Promise<Device[]> {
    this.dispatch(updateLoadingState(true));

    try {
      const devices = await this.api.unassociatedDeviceGetAll();

      this.dispatch(setUnassociatedDeviceList(devices));
      this.dispatch(updateLoadingState(false));

      return devices;
    } catch (_) {
      this.handleError('An error occurred while fetching devices.');
    }

    return [];
  }

  // update device info using api
  async updateDevice(
    deviceId: string,
    payload: IUpdateDevicePayload
  ): Promise<void> {
    this.dispatch(updateLoadingState(true));

    try {
      const updatedDevice = await this.api.update(deviceId, payload);

      // refetch all assets to get the updated device details
      void this.assetHandler.getAll();

      // update the unassociated device list
      this.dispatch(updateUnassociatedDeviceList(updatedDevice));

      this.handleSuccess('Device has been updated.');
      this.dispatch(updateLoadingState(false));
    } catch (_) {
      // show snackbar error
      this.handleError('An error occurred while updating the device.');
    }
  }

  /**
   * Gets all tags for associated devices from the dynamodb device_status for this particular customer
   * The data are then stored in the redux store.
   * @returns Promise<Device[]>
   *
   */
  async getDeviceLatestSensorDataAll(): Promise<Device[]> {
    let id: NodeJS.Timeout | null | undefined = null;
    this.dispatch(setIsLoadingSensorData(true));
    this.dispatch(setIsLoadingOperationalData(true));
    this.dispatch(updateLoadingState(true));

    try {
      const fetchSyncDataAsync = async () => {
        const [devices, operationalData] = await Promise.all([
          this.api.deviceLatestSensorDataAll(),
          this.api.deviceOperationalDataAll(),
        ]);
        // Start short polling with a delay
        const pollData = async () => {
          const [devices, operationalData] = await Promise.all([
            this.api.deviceLatestSensorDataAll(),
            this.api.deviceOperationalDataAll(),
          ]);

          this.dispatch(setDevicesWithTagList(devices));
          this.dispatch(setLatestOperationalData(operationalData));
          // Fetch data again after 5 minutes
          id = setTimeout(pollData, 300000);
          this.dispatch(updateLatestSensorPollingState(id));
        };

        // Initial delay before first data fetch
        id = setTimeout(pollData, 300000);
        this.dispatch(updateLatestSensorPollingState(id));

        // Dispatch operational data from initial load
        this.dispatch(setLatestOperationalData(operationalData));
        this.dispatch(setIsLoadingOperationalData(false));

        return devices;
      };

      const devices = await fetchSyncDataAsync();

      this.dispatch(setDevicesWithTagList(devices));
      this.dispatch(setIsLoadingSensorData(false));
      this.dispatch(updateLoadingState(false));
    } catch (e: unknown) {
      if (typeof e === 'object' && e !== null) {
        const response = (e as Record<string, any>).response;
        if (response?.data?.message === 'no devices found') {
          this.handleError('No device has been associated with an asset');
        } else {
          this.handleError('An error occurred while fetching devices.');
        }
      }
      this.dispatch(setIsLoadingSensorData(false));
      this.dispatch(setIsLoadingOperationalData(false));
      this.dispatch(updateLoadingState(false));
    }

    return [];
  }

  async getDeviceLatestSensorDataAllVarTime(
    latest: boolean
  ): Promise<Device[]> {
    try {
      const devices = await this.api.deviceLatestSensorDataAll(latest);
      this.dispatch(setDevicesWithTagList(devices));
      // Keep the raw latest=true snapshot (nulls preserved) so consumers can
      // prioritise live values and gate warnings on the live reading.
      if (latest) {
        this.dispatch(setLiveTagData(devices));
      }
      return devices;
    } catch (error) {
      this.handleApiError(error);
      return [];
    }
  }

  async startOperationalDataPolling(intervalMs = 30000): Promise<void> {
    this.dispatch(setIsLoadingOperationalData(true));

    const poll = async () => {
      try {
        const operationalData = await this.api.deviceOperationalDataAll();
        this.dispatch(setLatestOperationalData(operationalData));
        this.dispatch(setIsLoadingOperationalData(false));
      } catch (err) {
        console.error('Operational data poll failed:', err);
      } finally {
        const id = setTimeout(poll, intervalMs);
        this.dispatch(
          updateLatestSensorPollingState(id as unknown as NodeJS.Timeout)
        );
      }
    };

    // Immediate first call
    await poll();
  }

  private handleApiError(error: unknown) {
    if (typeof error === 'object' && error !== null) {
      const response = (error as Record<string, any>).response;
      if (response?.data?.message === 'no devices found') {
        this.handleError('No device has been associated with an asset');
      } else {
        this.handleError('An error occurred while fetching devices.');
      }
    }
  }
}
